import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Col, Form, Button, Card, Alert, FormGroup, Input, Label } from "reactstrap"
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { get, handleApiError, patch, upload } from "../../helpers/api_helper"
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api"
import toastr from "toastr"
import { useStore } from "../../contexts/StoreContext"

const googleMapsLibraries = ["places"]

const StoreManagement = (props) => {
  const { refreshStore } = useStore()
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedData, setUpdatedData] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const fileInputRef = useRef(null);
  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useJsApiLoader({
    id: "wevalo-partner-google-maps",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: googleMapsLibraries,
    language: "fr",
  });

  const fetchStoreDetail = async () => {
    setLoading(true);
    const responseStoreDetail = await get('store/mine');

    if (200 === responseStoreDetail.status) {
      setStore(responseStoreDetail.data);
      setUpdatedData(responseStoreDetail.data);
    }
    setLoading(false);
  }
  useEffect(() => {
    fetchStoreDetail();
  }, []);

  const handleChange = (e) => {
    setUpdatedData({ ...updatedData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setUpdatedData({ ...updatedData, image: e.target.files[0] });
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewPhoto(reader.result);
        setImageChanged(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await patch("stores/" + store.id, {
        "name": updatedData.name,
        "address": updatedData.address,
        "zipCode": updatedData.zipCode,
        "city": updatedData.city,
        "country": updatedData.country,
        "location": `POINT(${updatedData.location.longitude} ${updatedData.location.latitude})`,
        "acceptCash": updatedData.acceptCash,
        "translations": {
          fr: { description: updatedData.description, locale: "fr" },
          en: { description: updatedData.description, locale: "en" },
          ar: { description: updatedData.description, locale: "ar" },
        }
      });

      if (200 === response.status) {
        if (imageChanged) {
          const formDataImage = {'image': updatedData.image};
          const responseUpload = await upload(`stores/${store.id}/image/upload`, formDataImage);
          if (200 === responseUpload.status && 'ok' === responseUpload.data.result) {
            setImageChanged(false);
          }
        }
        toastr.success('Vos modifications ont été enregistrées avec succès.');
        await refreshStore()
      }
    } catch (error) {
      handleApiError(error.response.data);
    } finally {
      setSaving(false);
    }
  };

  const handleDragStoreLocation = (e) => {
    setStore({...store, location: { ...store.location, latitude: e.latLng.lat(), longitude: e.latLng.lng() }})
  }

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs
            title={props.t("Gestion du Magasin")}
            breadcrumbItem={props.t("Paramètres du Magasin")}
          />

          <Form onSubmit={handleSave}>
            <Row>
              <Col md={6}>
                <Row className="mb-2">
                  <Col md={12}>
                    <div className="py-3 position-relative">
                      <Label className="d-flex">Photo du magasin</Label>

                      <div className="position-relative d-inline-block">

                        {store.profilePhoto && (
                          <img
                            src={previewPhoto || store.profilePhoto}
                            alt="Profil"
                            className="rounded-circle border shadow"
                            style={{ width: "120px", height: "120px", objectFit: "cover", cursor: "pointer" }}
                            onClick={triggerFileSelect}
                          />
                        )}

                        <div
                          className="position-absolute top-50 start-50 translate-middle bg-dark text-white p-2 rounded-circle"
                          style={{
                            cursor: "pointer",
                            opacity: 0.8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "35px",
                            height: "35px",
                            transition: "opacity 0.2s",
                          }}
                          onClick={triggerFileSelect}
                        >
                          <i className="mdi mdi-camera font-size-18" />
                        </div>
                      </div>

                      <FormGroup className="d-none">
                        <Input
                          type="file"
                          accept="image/*"
                          innerRef={fileInputRef}
                          onChange={handleImageChange}
                        />
                      </FormGroup>
                    </div>
                  </Col>
                </Row>

                {/* Informations générales */}
                <Row className="mb-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>Nom du magasin</Label>
                      <Input type="text" name="name" value={updatedData.name} onChange={handleChange} required />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Activité</Label>
                      <Input type="text" name="activity" value={updatedData.activity?.name} disabled />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="mb-3">
                  <Label>Description</Label>
                  <Input type="textarea" name="description" rows={3} value={updatedData.description} onChange={handleChange} required />
                </FormGroup>

                {/* Adresse */}
                <Row className="mb-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label>Adresse</Label>
                      <Input type="text" name="address" value={updatedData.address} onChange={handleChange} required />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Code Postal</Label>
                      <Input type="text" name="zipCode" value={updatedData.zipCode} onChange={handleChange} required />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Ville</Label>
                      <Input type="text" name="city" value={updatedData.city} onChange={handleChange} required />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Pays</Label>
                      <Input type="text" name="country" value={updatedData.country} onChange={handleChange} required />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label className="align-items-center">Paiement en Espèce <span className="text-muted font-size-11 mx-1">Autorisez-vous que les clients paient leurs Paniers sur place en espèces ?</span></Label>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          id="yes"
                          name="positions"
                          className="form-check-input"
                          value="toast-top-full-width"
                          checked={updatedData.acceptCash === true}
                          onChange={() => setUpdatedData({ ...updatedData, acceptCash: true })}
                        />
                        <Label
                          className="form-check-label"
                          htmlFor="yes"
                        >
                          Oui
                        </Label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          id="no"
                          name="positions"
                          className="form-check-input"
                          value="toast-top-full-width"
                          checked={updatedData.acceptCash === false}
                          onChange={() => setUpdatedData({ ...updatedData, acceptCash: false })}
                        />
                        <Label
                          className="form-check-label"
                          htmlFor="no"
                        >
                          Non
                        </Label>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Site internet</Label>
                      <Input type="text" name="website" value={updatedData.website} onChange={handleChange} />
                    </FormGroup>
                  </Col>
                </Row>
              </Col>
              <Col md={6}>
                <div className="py-3">
                  <Label className="d-flex">📍 Localisation du Magasin</Label>
                  {googleMapsLoadError ? (
                    <p className="text-danger">Google Maps est momentanément indisponible.</p>
                  ) : store && store.location && isGoogleMapsLoaded ? (
                    <div>
                      <GoogleMap mapContainerStyle={{ "height": "500px"}} center={{lat: store.location.latitude, lng: store.location.longitude}} zoom={14}>
                        <Marker position={{lat: store.location.latitude, lng: store.location.longitude}} onDragEnd={(e) => {handleDragStoreLocation(e)}} draggable={true} />
                      </GoogleMap>
                    </div>
                  ) : (
                    <p className="text-muted">{store && store.location ? "Chargement de la carte..." : "Aucune localisation définie"}</p>
                  )}
                </div>
              </Col>
            </Row>
            <div className="text-center my-4">
              <Button
                type="submit"
                color="primary"
                className="w-50 d-flex align-items-center justify-content-center rounded-5 mx-auto"
                disabled={saving}
              >
                <i className="bx bx-save me-2"></i> {saving ? "Enregistrement..." : "Enregistrer les modifications"}
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(StoreManagement);
